import { FolderKanban, LayoutGrid, ListChecks } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { useApps } from "@/api/apps";
import { useSession } from "@/api/auth";
import { type Project, type ProjectRun, useProjects } from "@/api/projects";
import { useRuns } from "@/api/runs";
import { relativeTime } from "@/components/grid/cells";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage, classifyApiError } from "@/lib/api-error-kind";

/**
 * The dashboard (flowstack#945) — the landing surface, answering
 * "what is happening with my work right now?" in three panels: the
 * apps you can open, the projects with work in them, and the latest
 * runs.
 *
 * Three rules shape everything below.
 *
 * **It summarises; it never computes.** Every number here is a field
 * some endpoint already returns, or a rollup over a COMPLETE list
 * (`/api/apps` and `/api/projects` are unpaginated). Nothing is
 * assembled by paging the browser through a bounded feed — a total
 * derived that way is slow and quietly wrong the moment the data
 * outgrows the page size. Where a KPI had no source, it is ABSENT
 * and filed, not faked:
 *
 * - **who submitted a run** — the `runs` table has no user column at
 *   all, and both run routes synthesize `submitted_by` from the
 *   *requesting* session, so every row claims to be yours (#960).
 *   The "Latest work" rows therefore carry no "By" column, and this
 *   page is scoped and worded as your TENANT's work, not "your" work.
 * - **compute, started, duration** — `started_at` / `finished_at` /
 *   `duration_seconds` are hardcoded `None` on the wire and
 *   `provider` never leaves the database (#961), so those columns
 *   are omitted rather than rendered empty.
 * - **artifact counts** — `/api/projects` hardcodes `artifact_count:
 *   0` (#961), so the project rows show run counts only.
 *
 * **It is a summary, not a second source of truth.** Every panel
 * header links to the real page and every row links to its own
 * detail route. Nothing is reachable ONLY from here.
 *
 * **Each panel fails alone.** The three reads are independent
 * queries rendered through independent state machines
 * (loading / error / empty / content), so a 500 on one endpoint
 * degrades one card instead of blanking the landing page. Errors use
 * the same four-way classification the Analysis surface uses
 * (`classifyApiError`, flowstack#923) rather than one generic
 * "something went wrong".
 *
 * Scoping is the session's tenant, enforced server-side by
 * `SessionDep` → `tenant_context` on every endpoint read here — the
 * same path the Analysis surface uses. `tests/server/
 * test_dashboard_scoping.py` pins the negative case.
 */
export function DashboardPage(): JSX.Element {
  const { data: session } = useSession();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description={
          session
            ? `Work in ${session.tenant_display_name}.`
            : "Apps, projects and the latest runs."
        }
      />
      <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
        <AppsPanel />
        <ProjectsPanel />
        <LatestWorkPanel />
      </div>
    </div>
  );
}

// ---- Panel chrome ---------------------------------------------------

interface PanelProps {
  title: string;
  /** Where "the real page" is — the panel header links here. */
  to: string;
  /** Link text; names the destination so it is unambiguous out of context. */
  linkLabel: string;
  children: React.ReactNode;
}

/**
 * One dashboard card. The header's link is what keeps this a summary
 * rather than a second source of truth.
 *
 * Each panel is a labelled `region` landmark, not a bare card: the
 * dashboard is three independent things on one screen, so a user
 * navigating by landmark can reach "Latest work" directly instead of
 * arrowing through the apps list to find it. The label comes from the
 * panel's own heading via `aria-labelledby`, so the two can never
 * drift apart.
 */
function Panel({ title, to, linkLabel, children }: PanelProps): JSX.Element {
  const headingId = `dashboard-panel-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <Card
      elevation="raised"
      role="region"
      aria-labelledby={headingId}
      className="flex flex-col gap-4 p-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id={headingId}
          className="text-sm font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {title}
        </h2>
        <Link
          to={to}
          className="text-xs font-medium text-accent underline-offset-4 hover:underline"
        >
          {linkLabel}
        </Link>
      </div>
      {children}
    </Card>
  );
}

function PanelSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Four-way failure copy per panel (#923). `subject` names what could
 * not be read, so "we couldn't check" never reads as "there is
 * nothing here" — the two are different claims and only one of them
 * is the user's problem to act on.
 */
function describePanelError(
  error: unknown,
  subject: string
): { title: string; description: string } {
  const detail = apiErrorMessage(error);
  switch (classifyApiError(error)) {
    case "network":
      return {
        title: "Could not reach the server.",
        description: "Check your connection and try again.",
      };
    case "forbidden":
      return { title: `You don't have permission to view ${subject}.`, description: detail };
    case "not-found":
      return { title: `${subject} could not be found.`, description: detail };
    case "server":
      return { title: `The server failed to load ${subject}.`, description: detail };
    default:
      return { title: `Could not load ${subject}.`, description: detail };
  }
}

function PanelError({ error, subject }: { error: unknown; subject: string }): JSX.Element {
  const { title, description } = describePanelError(error, subject);
  return <EmptyState className="py-8" title={title} description={description} />;
}

// ---- Apps -----------------------------------------------------------

/**
 * The portals this session is entitled to open. `/api/apps` returns
 * the entitled subset (entitlement is decided server-side from the
 * Cognito groups), and it is unpaginated — so its length is the exact
 * count, not a page of one.
 *
 * Rows link to `/apps` rather than opening the portal directly: the
 * launcher already owns the launch semantics (new tab,
 * `noopener,noreferrer`, the locked/request-access branch), and
 * duplicating that here is the fork this dashboard is meant to avoid.
 */
function AppsPanel(): JSX.Element {
  const { data: apps, isLoading, isError, error } = useApps();

  return (
    <Panel title="Apps" to="/apps" linkLabel="All apps">
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your apps" />
      ) : !apps || apps.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<LayoutGrid className="h-7 w-7" aria-hidden />}
          title="No apps available to you yet."
          description="Ask a FlowStack admin for access, or open the launcher to request it from the owning team."
          cta={
            <Link
              to="/apps"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Open the launcher
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {apps.map((app) => (
            <li key={app.slug}>
              <Link to="/apps" className="flex flex-col rounded-md px-2 py-1.5 hover:bg-surface-2">
                <span className="truncate text-sm font-medium text-foreground">
                  {app.display_name}
                </span>
                {app.tagline ? (
                  <span className="truncate text-xs text-muted-foreground">{app.tagline}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---- Projects -------------------------------------------------------

/**
 * "Projects running" — `active_run_count` and `run_count` are
 * computed server-side per project (`/api/projects`), so this panel
 * reads them rather than counting runs in the browser. Sorted
 * active-first: a project with work in flight is the thing the user
 * came here to see.
 *
 * `artifact_count` is deliberately not rendered — the route hardcodes
 * it to 0 (#961), and a permanent zero is worse than no column.
 */
function ProjectsPanel(): JSX.Element {
  const { data: projects, isLoading, isError, error } = useProjects();

  const sorted = React.useMemo(
    () =>
      projects
        ? [...projects].sort(
            (a, b) =>
              b.active_run_count - a.active_run_count ||
              a.display_name.localeCompare(b.display_name)
          )
        : [],
    [projects]
  );
  // Exact, not a sample: /api/projects is unpaginated, so every
  // project the tenant has is in hand.
  const runningCount = sorted.filter((p) => p.active_run_count > 0).length;

  return (
    <Panel title="Projects running" to="/projects" linkLabel="All projects">
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your projects" />
      ) : sorted.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<FolderKanban className="h-7 w-7" aria-hidden />}
          title="No projects yet."
          description="A project is where runs and their artifacts live. Pilot tenants are seeded with one; contact your FlowStack administrator if you don't see it."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Go to Projects
            </Link>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {runningCount === 0
              ? `Nothing in flight across ${countLabel(sorted.length, "project")}.`
              : `${countLabel(runningCount, "project")} with work in flight.`}
          </p>
          <ul className="flex flex-col gap-1">
            {sorted.map((project) => (
              <li key={project.project_id}>
                <ProjectRow project={project} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function ProjectRow({ project }: { project: Project }): JSX.Element {
  return (
    <Link
      to={`/projects/${encodeURIComponent(project.slug)}`}
      className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-surface-2"
    >
      <span className="truncate text-sm font-medium text-foreground">{project.display_name}</span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {project.active_run_count > 0
          ? `${project.active_run_count} running / ${countLabel(project.run_count, "run")}`
          : countLabel(project.run_count, "run")}
      </span>
    </Link>
  );
}

// ---- Latest work ----------------------------------------------------

/** How many recent runs the summary shows before deferring to /runs. */
const LATEST_WORK_LIMIT = 6;

/**
 * The most recent runs, newest first. `/api/runs` is already ordered
 * most-recent-first server-side, so this slices rather than sorts.
 *
 * **No count is derived from this feed.** `/api/runs` is bounded
 * (`limit`, default 200), so "N runs in flight" computed here would be
 * silently capped — right on a pilot tenant, wrong on a busy one, and
 * wrong in the direction that under-reports. That is precisely the
 * "quietly wrong" aggregation #945 rules out, so the in-flight rollup
 * is left to the Projects panel, which reads a count the server
 * computed. Per-run state is still visible: every row carries its own
 * badge.
 *
 * No polling loop is added here. `useRuns` carries a 10s `staleTime`
 * and TanStack refetches on window focus; a dashboard that opened its
 * own interval would be a second poller against the same endpoint the
 * runs page already polls.
 */
function LatestWorkPanel(): JSX.Element {
  const { data: runs, isLoading, isError, error } = useRuns();
  const latest = runs ? runs.slice(0, LATEST_WORK_LIMIT) : [];

  return (
    <Panel title="Latest work" to="/runs" linkLabel="All runs">
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your latest runs" />
      ) : latest.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<ListChecks className="h-7 w-7" aria-hidden />}
          title="No runs yet."
          description="Submit a Tile or a Flow from a project — or from the SDK — and it will appear here as it progresses."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Pick a project to submit from
            </Link>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {/* States the truncation instead of implying these are all
              the runs there are. A bare list of six reads as "you have
              six runs" to anyone who does not already know better. */}
          <p className="text-sm text-muted-foreground">
            {`Your ${countLabel(latest.length, "most recent run")}.`}
          </p>
          <ul className="flex flex-col gap-1">
            {latest.map((run) => (
              <li key={run.run_id}>
                <RunRow run={run} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

/**
 * One run summary row. Reuses the signature `RunStateBadge` — the
 * same component the runs grid and the DAG nodes render — so a state
 * never looks like one thing here and another thing on the page this
 * links to.
 */
function RunRow({ run }: { run: ProjectRun }): JSX.Element {
  const workload = run.flow_name ?? run.tile_name ?? run.run_id;
  return (
    <Link
      to={`/runs/${encodeURIComponent(run.run_id)}`}
      className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-surface-2"
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-foreground">{workload}</span>
        <span className="truncate text-xs text-muted-foreground">
          {run.project_slug ?? "no project"} · <RelativeSubmitted at={run.submitted_at} />
        </span>
      </span>
      <RunStateBadge state={run.status} />
    </Link>
  );
}

/** "2m ago", with the absolute timestamp on hover. */
function RelativeSubmitted({ at }: { at: string }): JSX.Element {
  const when = new Date(at);
  return (
    <time dateTime={at} title={when.toLocaleString()}>
      {relativeTime(when)}
    </time>
  );
}

/** "1 run" / "3 runs" — pluralisation without a dependency. */
function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
